/**
 * Leviathan Web - Trucks Page
 * Quasar Prime LLC
 * Fleet Management Interface
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface Truck {
  id: string;
  unitNumber: string;
  name: string | null;
  vin: string | null;
  licensePlate: string | null;
  state: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  color: string | null;
  totalCapacity: number | null;
  compartments: number | null;
  status: string;
  currentDriverId: string | null;
  currentLocation: string | null;
  lastServiceDate: string | null;
  nextServiceDue: string | null;
  odometerReading: number | null;
  dotInspectionDue: string | null;
  isActive: boolean;
}

interface Stats {
  total: number;
  available: number;
  inUse: number;
  maintenance: number;
  outOfService: number;
  totalCapacity: number;
  serviceDueSoon: number;
  inspectionDueSoon: number;
}

interface StatCardProps {
  label: string;
  value: string | number;
  status?: 'operational' | 'warning' | 'critical';
}

/**
 * Compact stat card for truck metrics
 */
function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-2xl font-mono font-bold text-amber"
          style={{
            textShadow: '0 0 10px rgba(255, 149, 0, 0.6)',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

/**
 * Get status color and style
 */
function getStatusStyle(status: string): string {
  switch (status) {
    case 'AVAILABLE':
      return 'text-success-green bg-success-green/10';
    case 'IN_USE':
      return 'text-amber bg-amber/10';
    case 'MAINTENANCE':
      return 'text-warning-amber bg-warning-amber/10';
    case 'OUT_OF_SERVICE':
      return 'text-error-red bg-error-red/10';
    default:
      return 'text-text-secondary bg-text-secondary/10';
  }
}

/**
 * Get status icon
 */
function getStatusIcon(status: string): string {
  switch (status) {
    case 'AVAILABLE':
      return 'RDY';
    case 'IN_USE':
      return 'DLV';
    case 'MAINTENANCE':
      return 'MNT';
    case 'OUT_OF_SERVICE':
      return 'OOS';
    default:
      return '???';
  }
}

/**
 * Format date for display
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Check if date is within 30 days
 */
function isDueSoon(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  return date <= thirtyDays;
}

const TrucksPage = () => {
  const { accessToken } = useAuth();
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchTrucks();
  }, [search, statusFilter]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trucks/stats`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchTrucks = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${API_URL}/api/trucks?${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTrucks(data.trucks);
      }
    } catch (error) {
      console.error('Failed to fetch trucks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
  };

  return (
    <div className="space-y-6 relative">
      {/* Header with geometric frame */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Trucks
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">Fleet Management</p>
            </div>
            <Link
              to="/trucks/new"
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Truck</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <StatCard label="Total Fleet" value={stats.total} />
          <StatCard label="Available" value={stats.available} status="operational" />
          <StatCard
            label="In Use"
            value={stats.inUse}
            status={stats.inUse > 0 ? 'warning' : undefined}
          />
          <StatCard label="Maintenance" value={stats.maintenance} />
          <StatCard
            label="Out of Service"
            value={stats.outOfService}
            status={stats.outOfService > 0 ? 'critical' : undefined}
          />
          <StatCard
            label="Total Capacity"
            value={`${(stats.totalCapacity / 1000).toFixed(1)}K`}
          />
          <StatCard
            label="Service Due"
            value={stats.serviceDueSoon}
            status={stats.serviceDueSoon > 0 ? 'warning' : undefined}
          />
          <StatCard
            label="DOT Due"
            value={stats.inspectionDueSoon}
            status={stats.inspectionDueSoon > 0 ? 'warning' : undefined}
          />
        </div>
      )}

      {/* Filters Panel */}
      <GeometricPanel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Unit #, plate, make..."
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Statuses</option>
              <option value="AVAILABLE">Available</option>
              <option value="IN_USE">In Use</option>
              <option value="MAINTENANCE">Maintenance</option>
              <option value="OUT_OF_SERVICE">Out of Service</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GeometricPanel>

      {/* Trucks Grid */}
      {isLoading ? (
        <div className="p-12 text-center">
          <div className="text-text-tertiary font-mono animate-pulse">
            &gt; Loading fleet data...
          </div>
        </div>
      ) : trucks.length === 0 ? (
        <div className="p-12 text-center">
          <div className="text-text-tertiary font-mono text-lg mb-2">
            &gt; No trucks found
          </div>
          <p className="text-text-tertiary font-mono text-sm">
            {search || statusFilter
              ? 'Try adjusting your search filters'
              : 'Add your first truck to get started'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trucks.map((truck) => (
            <Link
              key={truck.id}
              to={`/trucks/${truck.id}`}
              className="relative stat-card group"
            >
              <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
              <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

              <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon hover:border-amber/60 transition-all">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="text-3xl">
                      {truck.status === 'IN_USE' ? '🚛' : truck.status === 'MAINTENANCE' ? '🔧' : '🚚'}
                    </div>
                    <div>
                      <div className="text-text-primary font-mono font-semibold text-xl">
                        {truck.unitNumber}
                      </div>
                      {truck.name && (
                        <div className="text-text-tertiary font-mono text-sm">"{truck.name}"</div>
                      )}
                    </div>
                  </div>
                  <span
                    className={`${getStatusStyle(truck.status)} font-mono text-xs uppercase tracking-wider font-semibold px-2 py-1 rounded`}
                  >
                    {getStatusIcon(truck.status)}
                  </span>
                </div>

                {/* Vehicle Info */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <span className="text-text-tertiary font-mono text-xs uppercase tracking-widest">
                      Vehicle
                    </span>
                    <div className="text-text-primary font-mono text-sm">
                      {[truck.year, truck.make, truck.model].filter(Boolean).join(' ') || '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-text-tertiary font-mono text-xs uppercase tracking-widest">
                      Plate
                    </span>
                    <div className="text-text-primary font-mono text-sm">
                      {truck.licensePlate
                        ? `${truck.licensePlate}${truck.state ? ` (${truck.state})` : ''}`
                        : '-'}
                    </div>
                  </div>
                </div>

                {/* Capacity */}
                <div className="mb-4">
                  <span className="text-text-tertiary font-mono text-xs uppercase tracking-widest">
                    Capacity
                  </span>
                  <div
                    className="text-amber font-mono font-semibold text-lg"
                    style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.4)' }}
                  >
                    {truck.totalCapacity
                      ? `${truck.totalCapacity.toLocaleString()} gal`
                      : '-'}
                    {truck.compartments && (
                      <span className="text-text-tertiary text-sm ml-2">
                        ({truck.compartments} comp)
                      </span>
                    )}
                  </div>
                </div>

                {/* Odometer */}
                {truck.odometerReading && (
                  <div className="text-text-tertiary font-mono text-xs mb-2">
                    Odometer: {truck.odometerReading.toLocaleString()} mi
                  </div>
                )}

                {/* Service Due Warning */}
                {truck.nextServiceDue && (
                  <div
                    className={`font-mono text-xs ${isDueSoon(truck.nextServiceDue) ? 'text-warning-amber' : 'text-text-tertiary'}`}
                  >
                    Service due: {formatDate(truck.nextServiceDue)}
                    {isDueSoon(truck.nextServiceDue) && ' !'}
                  </div>
                )}

                {/* DOT Inspection Warning */}
                {truck.dotInspectionDue && isDueSoon(truck.dotInspectionDue) && (
                  <div className="font-mono text-xs text-warning-amber mt-1">
                    DOT due: {formatDate(truck.dotInspectionDue)} !
                  </div>
                )}

                {/* Current Location */}
                {truck.currentLocation && truck.status === 'IN_USE' && (
                  <div className="text-text-tertiary font-mono text-xs mt-2 border-t border-panel-border pt-2">
                    Location: {truck.currentLocation}
                  </div>
                )}

                {/* Hover Arrow */}
                <div className="absolute bottom-4 right-4 text-steel opacity-0 group-hover:opacity-100 transition-opacity font-mono">
                  &rarr;
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrucksPage;
